/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Checkbox, FormGroup, FormControlLabel } from '@mui/material';
import Typography from '@mui/material/Typography';
import { textStyleWithMargin } from './LayerInfoText';
import { useWebmapReactTranslation } from '../../utils/i18n';
import { LegendLayout } from '../Legend';

export interface LayerInfoLegendProps {
  title: string;
  legendURL?: string;
  showLayerLegend: boolean;
  onToggleLayerLegendVisibility: (showLegend: boolean) => void;
}

export const LayerInfoLegend: React.FC<LayerInfoLegendProps> = ({
  title,
  legendURL,
  showLayerLegend,
  onToggleLayerLegendVisibility,
}: LayerInfoLegendProps) => {
  const { t } = useWebmapReactTranslation();
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography sx={textStyleWithMargin} variant="subtitle2">
          {t('webmap-react-legend')}
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={showLayerLegend}
                onChange={() => {
                  onToggleLayerLegendVisibility(!showLayerLegend);
                }}
              />
            }
            label={t('webmap-react-show-layer-legend')}
            sx={textStyleWithMargin}
          />
        </FormGroup>
      </Box>
      <LegendLayout title={title}>
        {legendURL ? <img alt={title} src={legendURL} /> : '-'}
      </LegendLayout>
    </>
  );
};
